import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { differenceInCalendarDays, parseISO } from "date-fns";
import api from "@/lib/api";
import { num, shortDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Factory, CalendarDays, Pencil } from "lucide-react";
import { toast } from "sonner";
import { LabFields } from "@/components/NewBatchDialog";

const days = (a, b) => Math.max(0, differenceInCalendarDays(b ? parseISO(b) : new Date(), parseISO(a)));

export const BatchSizes = ({ sizes }) =>
  sizes?.length ? (
    <div className="mt-3 flex flex-wrap gap-1.5" data-testid="batch-sizes">
      {sizes.map((s) => (
        <span key={s.size} className="rounded border border-border/60 px-2 py-0.5 font-mono-erp text-[11px]">
          {s.size} <span className="text-muted-foreground">×</span> {num(s.quantity)}
        </span>
      ))}
    </div>
  ) : null;

export const PhaseTimeline = ({ history, phaseMeta }) => {
  if (!history?.length) return null;
  return (
    <div className="mt-3 space-y-1 rounded-md border border-border/60 p-3" data-testid="batch-timeline">
      <p className="mb-1 font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">Durata fasi</p>
      {history.map((h, i) => {
        const next = history[i + 1];
        const current = !next && h.phase !== "completato";
        return (
          <div key={`${h.phase}-${h.date}`} className="flex items-center justify-between text-xs">
            <span>{phaseMeta(h.phase).label} <span className="text-muted-foreground">· dal {shortDate(h.date)}</span></span>
            {h.phase !== "completato" && (
              <span className={current ? "font-medium text-primary" : "font-medium"}>
                {days(h.date, next?.date)} gg{current ? " (in corso)" : ""}
              </span>
            )}
          </div>
        );
      })}
      {history.length > 1 && (
        <div className="flex justify-between border-t border-border/60 pt-1 text-xs font-semibold">
          <span>Totale</span><span>{days(history[0].date, history[history.length - 1].phase === "completato" ? history[history.length - 1].date : null)} gg</span>
        </div>
      )}
    </div>
  );
};

export const LabInfo = ({ batch, ids }) => {
  const qc = useQueryClient();
  const [edit, setEdit] = useState(false);
  const [lab, setLab] = useState(batch.lab || "");
  const [date, setDate] = useState(batch.expected_delivery || "");
  const { data: labs = [] } = useQuery({ queryKey: ["labs"], enabled: edit, queryFn: async () => (await api.get("/production/labs")).data });
  const save = useMutation({
    mutationFn: async () => { for (const id of ids || [batch.id]) await api.patch(`/production/${id}`, { lab, expected_delivery: date }); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["production"] }); qc.invalidateQueries({ queryKey: ["labs"] }); setEdit(false); toast.success("Laboratorio aggiornato"); },
  });
  const late = batch.expected_delivery && batch.phase === "confezione" && batch.expected_delivery < new Date().toISOString().slice(0, 10);

  if (edit) {
    return (
      <div className="mt-3 grid grid-cols-2 gap-2 rounded-md border border-border/60 p-3">
        <LabFields lab={lab} date={date} onLab={setLab} onDate={setDate} labs={labs} testid={`lab-${batch.batch_number}`} />
        <Button size="sm" className="col-span-2" onClick={() => save.mutate()} disabled={save.isPending} data-testid={`save-lab-${batch.batch_number}`}>Salva</Button>
      </div>
    );
  }
  return (
    <div className="mt-3 flex items-start justify-between rounded-md border border-border/60 p-3 text-xs" data-testid={`lab-info-${batch.batch_number}`}>
      <div className="space-y-1">
        <p className="flex items-center gap-1.5"><Factory className="h-3.5 w-3.5 text-muted-foreground" />{batch.lab || <span className="text-muted-foreground">Laboratorio non indicato</span>}</p>
        <p className={`flex items-center gap-1.5 ${late ? "text-rose-400" : ""}`}>
          <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
          {batch.expected_delivery ? `Consegna prevista ${shortDate(batch.expected_delivery)}${late ? " · in ritardo" : ""}` : <span className="text-muted-foreground">Consegna non indicata</span>}
        </p>
      </div>
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEdit(true)} data-testid={`edit-lab-${batch.batch_number}`}><Pencil className="h-3.5 w-3.5" /></Button>
    </div>
  );
};
